import React, { useMemo } from 'react'
import { styled } from '@mui/material/styles'
import { CircularProgress } from '@mui/material'
import { AlertType } from './types/AlertType'
import { GroupType } from './types/GroupType'
import { AlertTable } from './AlertTable'
import { Alert } from './types/Alert'

import {
  groupByProductId,
  groupByFieldIdAndMessage,
} from './util/alertGroupingUtils'

const StyledDiv = styled('div')({
  overflowX: 'hidden',
  overflowY: 'auto',
  height: '100%',
  maxHeight: '100%',
})

const getDisplayStyle = (
  loading: boolean,
  groupType: GroupType,
  wantedGroupType: GroupType,
) => {
  if (loading || groupType !== wantedGroupType) {
    return { display: 'none' }
  }
  return {}
}

export interface AlertTableContainerProps {
  groupType: GroupType
  alertType: AlertType
  loading?: boolean
  alerts: Alert[]
  onAlertClick?: (args: { fieldId: string; productId: string }) => void
}

export const AlertTableContainer = ({
  groupType,
  alertType,
  loading = false,
  alerts,
  onAlertClick,
}: AlertTableContainerProps) => {
  const alertsGroupedByProductId: Alert[][] = useMemo(
    () => groupByProductId(alerts),
    [alerts],
  )

  const alertsGroupedByFieldIdAndMessage: Alert[][] = useMemo(
    () => groupByFieldIdAndMessage(alerts),
    [alerts],
  )

  return (
    <>
      {loading && (
        <StyledDiv style={{ textAlign: 'center' }}>
          <CircularProgress
            data-testid="alertTableLoading"
            size={24}
            thickness={4}
            style={{ padding: '8px' }}
          />
        </StyledDiv>
      )}
      <StyledDiv
        id="productGroupedAlerts"
        data-testid="productGroupedAlerts"
        style={getDisplayStyle(loading, groupType, GroupType.Product)}
      >
        <AlertTable
          groupType={GroupType.Product}
          alertType={alertType}
          alerts={alertsGroupedByProductId}
          onAlertClick={onAlertClick}
        />
      </StyledDiv>
      <StyledDiv
        id="fieldAndMessageGroupedAlerts"
        data-testid="fieldAndMessageGroupedAlerts"
        style={getDisplayStyle(loading, groupType, GroupType.FieldAndMessage)}
      >
        <AlertTable
          groupType={GroupType.FieldAndMessage}
          alertType={alertType}
          alerts={alertsGroupedByFieldIdAndMessage}
          onAlertClick={onAlertClick}
        />
      </StyledDiv>
      <StyledDiv
        id="ungroupedAlerts"
        data-testid="ungroupedAlerts"
        style={getDisplayStyle(loading, groupType, GroupType.None)}
      >
        <AlertTable
          groupType={GroupType.None}
          alertType={alertType}
          alerts={alerts}
          onAlertClick={onAlertClick}
        />
      </StyledDiv>
    </>
  )
}
